import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { ChampFiltre, ChampRecherche } from '../ui/Onglets';
import { ErreurApi } from '../../lib/api';
import { useFinanceurs, type Financeur } from '../../lib/useFinancements';
import { ModaleFinanceur } from './ModaleFinanceur';

type FiltreEtat = '' | 'A_COMPLETER' | 'PRETS' | 'DESACTIVES';

const FILTRES: { valeur: FiltreEtat; libelle: string }[] = [
  { valeur: '', libelle: 'Tous les financeurs' },
  { valeur: 'A_COMPLETER', libelle: 'À compléter' },
  { valeur: 'PRETS', libelle: 'Prêts à facturer' },
  { valeur: 'DESACTIVES', libelle: 'Désactivés' },
];

function cle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function retenu(financeur: Financeur, filtre: FiltreEtat, recherche: string): boolean {
  if (filtre === 'A_COMPLETER' && (!financeur.actif || financeur.etat.pret)) return false;
  if (filtre === 'PRETS' && (!financeur.actif || !financeur.etat.pret)) return false;
  if (filtre === 'DESACTIVES' && financeur.actif) return false;
  const cherche = cle(recherche.trim());
  if (!cherche) return true;
  return [financeur.raisonSociale, financeur.code, financeur.siret ?? '', financeur.ville ?? ''].some((valeur) =>
    cle(valeur).includes(cherche),
  );
}

/**
 * Fiches des financeurs, clients des factures (TF-406). Les OPCO et France
 * Travail sont installés sans coordonnées : le pôle facturation les complète
 * ici, et ajoute un financeur qui manque. L'état — prête à facturer, ou ce qui
 * manque — vient de l'API, la règle même que l'émission appliquera.
 *
 * `aOuvrir` : identifiant d'une fiche à ouvrir d'emblée, quand on arrive du
 * dossier par « Compléter la fiche du financeur ».
 */
export function OngletFinanceurs({
  aOuvrir,
  onOuverte,
}: {
  aOuvrir: string | null;
  onOuverte: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useFinanceurs();
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<FiltreEtat>('');
  const [edition, setEdition] = useState<{ financeur: Financeur | null } | null>(null);

  useEffect(() => {
    if (!aOuvrir || !data) return;
    const financeur = data.financeurs.find((ligne) => ligne.id === aOuvrir);
    if (financeur) setEdition({ financeur });
    onOuverte();
  }, [aOuvrir, data, onOuverte]);

  const lignes = useMemo(
    () => (data?.financeurs ?? []).filter((financeur) => retenu(financeur, filtre, recherche)),
    [data?.financeurs, filtre, recherche],
  );
  const compteurs = data?.compteurs;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Compteur libelle="Financeurs actifs" valeur={compteurs?.actifs ?? '—'} detail="OPCO, France Travail, autres" ton="navy" />
        <Compteur
          libelle="À compléter"
          valeur={compteurs?.aCompleter ?? '—'}
          detail="adresse ou SIRET manquant"
          ton="warm"
        />
        <Compteur libelle="Prêts à facturer" valeur={compteurs?.prets ?? '—'} detail="fiche complète" ton="mint" />
      </div>

      <Carte
        titre="Financeurs"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un financeur"
              placeholder="Nom, SIRET, ville"
            />
            <ChampFiltre
              valeur={filtre}
              onChange={(valeur) => setFiltre(valeur as FiltreEtat)}
              etiquette="Filtrer les financeurs"
            >
              {FILTRES.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
            <BoutonPrincipal onClick={() => setEdition({ financeur: null })} disabled={!data}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Ajouter un financeur
            </BoutonPrincipal>
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={error instanceof ErreurApi ? error.message : 'Les financeurs n’ont pas pu être chargés.'}
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des financeurs…" />}
        {data && lignes.length === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            Aucun financeur ne correspond aux filtres actifs.
          </p>
        )}

        {data && lignes.length > 0 && (
          <ul className="divide-y divide-gray-100 md:hidden">
            {lignes.map((financeur) => (
              <li key={financeur.id} className="space-y-2 px-4 py-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <Identite financeur={financeur} />
                  <Etat financeur={financeur} />
                </div>
                <Coordonnees financeur={financeur} />
                <p className="text-[11px] text-codialis-gray">
                  Règlement à {financeur.delaiPaiementJours} jours · {libelleContrats(financeur.contrats)}
                </p>
                <BoutonCorriger financeur={financeur} onClick={() => setEdition({ financeur })} />
              </li>
            ))}
          </ul>
        )}

        {data && lignes.length > 0 && (
          <div className="relative hidden overflow-x-auto md:block">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Financeur', 'Coordonnées de facturation', 'Règlement', 'État', ''].map((colonne, index) => (
                    <th
                      key={colonne || index}
                      className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray lg:px-4"
                    >
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((financeur) => (
                  <tr key={financeur.id} className="align-top transition hover:bg-gray-50">
                    <td className="px-3 py-3 lg:px-4">
                      <Identite financeur={financeur} />
                    </td>
                    <td className="max-w-[320px] px-3 py-3 lg:px-4">
                      <Coordonnees financeur={financeur} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-codialis-gray lg:px-4">
                      <div className="font-semibold text-codialis-navy">{financeur.delaiPaiementJours} jours</div>
                      <div>{libelleContrats(financeur.contrats)}</div>
                    </td>
                    <td className="max-w-[220px] px-3 py-3 lg:px-4">
                      <Etat financeur={financeur} />
                    </td>
                    <td className="px-3 py-3 text-right lg:px-4">
                      <BoutonCorriger financeur={financeur} onClick={() => setEdition({ financeur })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {data && (
        <ModaleFinanceur
          financeur={edition?.financeur ?? null}
          types={data.types}
          ouverte={edition !== null}
          onFermer={() => setEdition(null)}
        />
      )}
    </>
  );
}

function libelleContrats(contrats: number): string {
  if (contrats === 0) return 'aucun contrat';
  return `${contrats} contrat${contrats > 1 ? 's' : ''}`;
}

function Identite({ financeur }: { financeur: Financeur }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="break-words font-semibold text-codialis-navy">{financeur.raisonSociale}</div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <Pastille texte={financeur.typeLibelle} ton="info" />
        {!financeur.actif && <Pastille texte="Désactivé" ton="neutral" />}
      </div>
    </div>
  );
}

function Coordonnees({ financeur }: { financeur: Financeur }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <div className="whitespace-pre-line break-words text-codialis-navy">
        {financeur.adresseImprimee ?? <span className="text-codialis-gray">Adresse non renseignée</span>}
      </div>
      <div className="break-all font-mono text-[11px] text-codialis-gray">
        {financeur.siret ? `SIRET ${financeur.siret}` : 'SIRET non renseigné'}
      </div>
      {financeur.emailFacturation && (
        <div className="break-all text-[11px] text-codialis-gray">{financeur.emailFacturation}</div>
      )}
    </div>
  );
}

function Etat({ financeur }: { financeur: Financeur }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <Pastille texte={financeur.etat.libelle} ton={financeur.etat.ton} />
      {financeur.etat.detail && <p className="text-[11px] text-amber-700">{financeur.etat.detail}</p>}
    </div>
  );
}

function BoutonCorriger({ financeur, onClick }: { financeur: Financeur; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline"
    >
      <Pencil className="h-3.5 w-3.5" aria-hidden />
      {financeur.etat.pret ? 'Corriger' : 'Compléter'}
      <span className="sr-only"> la fiche de {financeur.raisonSociale}</span>
    </button>
  );
}
